import { GameConfig } from './models/game-config.model';
import { CardModel, CardState, GameViewModel, HandModel, HandState, SideModel, SideState } from './models/game-view.model';
import { CardValue } from '../models/communication/cards.enum';

/**
 * Transforms the config (`GameConfig`) to the (`GameViewModel`) data
 * used by `GameBase` and the component templates to generate the UI.
 * The data is organized as a hierarchy of one or more elements:
 *
 * -> Side
 * ---> Hand
 * -----> Card
 */

export const generateGameViewModel = (config: GameConfig): GameViewModel => {
    const title = { text: '', isSpecial: false };
    const shrinkMode = false;
    const cards = generateCards(config);
    const hands = generateHands(config, cards);
    const sides = generateSides(config, hands);

    return addParentRefs({
        title,
        cards,
        hands,
        sides,
        shrinkMode
    });
};

const generateCards = (config: GameConfig): CardModel[] => {
    const cards = [];

    for (const sideData of config.sides) {
        for (const handData of sideData.hands) {
            for (const cardData of handData.cards) {
                // We need to assert because the property `parentHand` is missing.
                // It will be added in `addParentRefs`.
                cards.push({
                    position: cardData.position,
                    state: cardData.state ?? CardState.Empty,
                    value: CardValue.Unknown,
                    isRotated: cardData.isRotated ?? false,
                    isCompact: cardData.isCompact ?? false
                } as CardModel);
            }
        }
    }

    cards.sort((a, b) => a.position - b.position);
    return cards;
};

const generateHands = (config: GameConfig, cards: CardModel[]): HandModel[] => {
    const hands = [];

    for (const [sideIndex, sideData] of config.sides.entries()) {
        for (const handData of sideData.hands) {
            // We need to assert because the property `parentSide` is missing.
            // It will be added in `addParentRefs`.
            hands.push({
                label: handData.label,
                state: HandState.Default,
                sideIndex,
                handValueKey: handData.handValueKey,
                cards: handData.cards.map((card) => cards[card.position]),
                amount: { value: 0, visible: false }
            } as HandModel);
        }
    }

    return hands;
};

const generateSides = (config: GameConfig, hands: HandModel[]): SideModel[] => {
    return config.sides.map((sideData, sideIndex) => {
        const handsInSide: HandModel[] = hands.filter((hand) => hand.sideIndex === sideIndex);
        return {
            position: sideData.position,
            state: SideState.Default,
            label: sideData.label,
            hands: handsInSide
        };
    });
};

const addParentRefs = (data: GameViewModel): GameViewModel => {
    for (const side of data.sides) {
        for (const hand of side.hands) {
            hand.parentSide = side;
            for (const card of hand.cards) {
                card.parentHand = hand;
            }
        }
    }
    return data;
};

export const removeParentRefs = (data: GameViewModel): GameViewModel => {
    for (const side of data.sides) {
        for (const hand of side.hands) {
            (hand as Partial<HandModel>).parentSide = undefined;
            for (const card of hand.cards) {
                (card as Partial<CardModel>).parentHand = undefined;
            }
        }
    }
    return data;
};

export const cloneData = (data: GameViewModel): GameViewModel => {
    const title = { ...data.title };
    const cards = data.cards.map((obj) => ({ ...obj }));
    const hands = data.hands.map((hand) => ({
        ...hand,
        cards: hand.cards.map((card) => cards[card.position])
    }));
    const sides = data.sides.map((side, sideIndex) => ({
        ...side,
        hands: hands.filter((hand) => hand.sideIndex === sideIndex)
    }));

    return addParentRefs({
        ...data,
        title,
        cards,
        hands,
        sides
    });
};
